import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { CircularProgress, TextField, InputAdornment } from "@mui/material"; // Corrected: Removed MenuItem here
import WarningIcon from '@mui/icons-material/Warning';
import { red } from '@mui/material/colors';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDButton from "components/MDButton";
import IconButton from "@mui/material/IconButton";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from "sweetalert2";
import api from "core/interceptors/api";
import { useNavigate } from "react-router-dom";
import LogoutButton from "layouts/logout";
import IncentiveAddModal from "./incentive_add_modal";
import IncentiveEditModal from "./incentive_edit_modal";
import SearchIcon from '@mui/icons-material/Search';

export default function IncentiveManagement() {
  // Pagination and Search states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredIncentives, setFilteredIncentives] = useState([]); // Data after search filtering

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [editInput, setEditInput] = useState({});
  const [loading, setLoading] = useState(true);
  const [incentives, setIncentives] = useState([]); // All fetched data
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchIncentives();
  }, []);

  // Effect for global search filtering
  useEffect(() => {
    const lowerCaseQuery = searchQuery.toLowerCase();
    const newFilteredIncentives = incentives.filter(incentive => {
      const percentageMatch = String(incentive.percentage)?.toLowerCase().includes(lowerCaseQuery);
      const descriptionMatch = incentive.description?.toLowerCase().includes(lowerCaseQuery);
      const statusMatch = incentive.status?.toLowerCase().includes(lowerCaseQuery);
      return percentageMatch || descriptionMatch || statusMatch;
    });
    setFilteredIncentives(newFilteredIncentives);
    setPage(0); // Reset to the first page of filtered results
  }, [searchQuery, incentives]); // Depend on searchQuery and the full incentives list

  const handleClose = () => {
    setEditInput({});
    setOpenAddModal(false);
    setOpenEditModal(false);
    setOpenDeleteModal(false);
    fetchIncentives(); // Re-fetch data to update the table
  };

  // Pagination handlers
  const handleRowsPerPageChange = (event, newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when rows per page changes
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Memoized data for the current page, based on filtered incentives
  const paginatedTableData = useMemo(() => {
    return filteredIncentives.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredIncentives, page, rowsPerPage]);

  const columns = useMemo(() => [
    {
      Header: "S.No",
      accessor: "s_no",
      align: "center",
      Cell: ({ row }) => (page * rowsPerPage) + row.index + 1, // Calculate serial number
      disableSortBy: true,
    },
    { Header: "Percentage", accessor: "percentage", align: "center" },
    { Header: "Description", accessor: "description", align: "center" },
    { Header: "Role", accessor: "role", align: "center" },
    { Header: "Status", accessor: "status", align: "center" },
    {
      Header: "Actions",
      accessor: "actions",
      align: "center",
      Cell: ({ row }) => (
        <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <IconButton color="success" onClick={() => {
            setEditInput(row.original);
            setOpenEditModal(true);
          }}>
            <EditIcon />
          </IconButton>
          <IconButton color="error" onClick={() => {
            setDeleteId(row.original.id);
            setOpenDeleteModal(true);
          }}>
            <DeleteIcon />
          </IconButton>
        </div>
      )
    }
  ], [page, rowsPerPage]); // Dependencies for useMemo on columns

  // Data for DataTable, derived from paginated data
  const data = useMemo(() => paginatedTableData.map((item) => ({
    ...item
  })), [paginatedTableData]);

  const fetchIncentives = async () => {
    setLoading(true);
    try {
      const res = await api.get("/incentives");
      setIncentives(res.data.incentives || []); // Set all fetched incentives
    } catch (error) {
      console.error("Error fetching incentives:", error);
      Swal.fire("Error", "Failed to fetch incentives from server.", "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteIncentive = async (e) => {
    e.preventDefault();
    try {
      handleClose(); // Close modal and trigger re-fetch
      const res = await api.delete(`/incentives/${deleteId}`);
      Swal.fire("Success", res.data.message || "Incentive deleted successfully.", "success");
    } catch (err) {
      console.error("Error deleting incentive:", err);
      Swal.fire("Error", "Failed to delete incentive from server.", "error");
    } finally {
      // fetchIncentives() is called by handleClose already
    }
  };

  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end" alignItems="center">
        <LogoutButton onLogout={() => navigate("/login")} />
      </MDBox>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
                display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" color="white">Incentive Management</MDTypography>
                {/* Global Search Bar */}

              </MDBox>
              <MDBox display="flex" justifyContent="space-between" sx={{ mt: 4, mr: 4 }}>
                <TextField
                  label="Search incentives..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'black' }} />
                      </InputAdornment>
                    ),
                    style: { color: 'black' }
                  }}
                  InputLabelProps={{
                    style: { color: 'black' }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: 'black' },
                      '&:hover fieldset': { borderColor: 'black' },
                      '&.Mui-focused fieldset': { borderColor: 'black' },
                    },
                    width: '250px',
                    ml: 2
                  }}
                />
                <MDBox display="flex" flexDirection="column" sx={{ mr: 4 }}>
                  <MDButton
                    color="success"
                    onClick={() => setOpenAddModal(true)}
                    
                  >
                    Add Incentive
                  </MDButton>
                
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{ mt: 1 }} // Add top margin for spacing from button
                    >
                     
                    </MDTypography>
                
                </MDBox>

              </MDBox>
              <MDBox p={3} display="flex" justifyContent="center" alignItems="center" minHeight="50svh">
                {loading ? (
                  <CircularProgress color="primary" />
                ) : (
                  <DataTable
                    table={{ columns, rows: data }}
                    isSorted={true}
                    entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
                    showTotalEntries={true}
                    canSearch={false} // Disable internal DataTable search, as we handle it globally
                    noEndBorder
                    pagination={{
                      variant: "contained",
                      color: "dark",
                      page: page,
                      onPageChange: handlePageChange,
                      rowsPerPage: rowsPerPage,
                      onRowsPerPageChange: handleRowsPerPageChange,
                      count: filteredIncentives.length, // Count based on filtered data
                    }}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <IncentiveAddModal open={openAddModal} handleClose={handleClose} />
      <IncentiveEditModal open={openEditModal} handleClose={handleClose} input={editInput} setInput={setEditInput} />

      <Dialog open={openDeleteModal} onClose={handleClose}>
        <MDBox component="form" onSubmit={deleteIncentive}>
          <DialogTitle>
            <Grid container spacing={2} alignItems="center">
              <Grid item><WarningIcon style={{ color: red[700], fontSize: 40 }} /></Grid>
              <Grid item><MDTypography variant="h6">Confirm Delete</MDTypography></Grid>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2} style={{ backgroundColor: red[50], borderLeft: `5px solid ${red[700]}` }}>
              <MDTypography>Are you sure you want to delete this incentive?</MDTypography>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleClose} style={{ color: "gray" }}>Cancel</MDButton>
            <MDButton type="submit" color="primary">Delete</MDButton>
          </DialogActions>
        </MDBox>
      </Dialog>
    </DashboardLayout>
  );
}